import React from 'react';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDollarSign,
    faEye,
    faUsers,
    faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import {
    ArrowUpOutlined,
    ArrowDownOutlined
} from '@ant-design/icons';
import { COLORS } from "../../../constants/colors";
import {
    MetricCard,
    MetricValue,
    MetricLabel,
    PercentageChange,
    ContentWrapper,
    MetricInfo,
    SparklineWrapper
} from '../styled';
import { Sparkline } from '../Sparkline';
import { calculatePercentageChange } from '../utils';

const METRIC_CONFIG = {
    revenue: {
        title: "Revenue",
        icon: faDollarSign,
        color: COLORS.$green,
        dataKey: 'netRevenue',
        getValue: (metrics) => metrics.netRevenue,
        getPreviousValue: (metrics) => metrics.previousNetRevenue,
        formatValue: (value) => `$${value.toFixed(2)}`,
        tooltipTitle: "Net Revenue Trend"
    },
    visits: {
        title: "Store Visits",
        icon: faEye,
        color: COLORS.$blue,
        dataKey: 'visits',
        getValue: (metrics) => metrics.totalVisits,
        getPreviousValue: (metrics) => metrics.previousVisits,
        formatValue: (value) => value.toLocaleString(),
        tooltipTitle: "Visits Trend"
    },
    leads: {
        title: "Leads",
        icon: faUsers,
        color: COLORS.$purple,
        dataKey: 'emailLeads',
        getValue: (metrics) => metrics.totalLeads,
        getPreviousValue: (metrics) => metrics.previousLeads,
        formatValue: (value) => value.toLocaleString(),
        tooltipTitle: "Leads Trend"
    },
    conversion: {
        title: "Conversion Rate",
        icon: faChartLine,
        color: COLORS.$orange,
        dataKey: 'conversionRate',
        getValue: (metrics) => metrics.conversionRate,
        getPreviousValue: (metrics) => metrics.previousConversionRate,
        formatValue: (value) => `${value.toFixed(1)}%`,
        tooltipTitle: "Conversion Rate Trend"
    }
};

export const MetricCardComponent = ({
    type,
    metrics,
    chartDataKey,
    setChartDataKey,
    filteredData
}) => {
    const config = METRIC_CONFIG[type];
    const value = config.getValue(metrics);
    const previousValue = config.getPreviousValue(metrics);
    const percentageChange = calculatePercentageChange(value, previousValue);

    const sparklineData = type === 'conversion'
        ? filteredData.map(item => ({
            ...item,
            conversionRate: item.visits > 0 ? (item.emailLeads / item.visits) * 100 : 0
        }))
        : filteredData;

    return (
        <MetricCard 
            isActive={chartDataKey === config.dataKey} 
            color={config.color} 
            onClick={() => setChartDataKey(config.dataKey)}
        >
            <ContentWrapper>
                <MetricInfo>
                    <MetricLabel>
                        <FontAwesomeIcon icon={config.icon} />
                        <span>{config.title}</span>
                    </MetricLabel>
                    <MetricValue positive={value >= 0}>
                        {config.formatValue(value)}
                    </MetricValue>

                    {percentageChange !== 0 && (
                        <Tooltip title="Change from previous period">
                            <PercentageChange positive={percentageChange >= 0}>
                                {percentageChange >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                {Math.abs(percentageChange)?.toFixed(1)}%
                            </PercentageChange>
                        </Tooltip>
                    )}
                </MetricInfo>
                <Tooltip title={config.tooltipTitle}>
                    <SparklineWrapper>
                        <Sparkline
                            data={sparklineData}
                            dataKey={config.dataKey}
                            color={config.color}
                            tooltipFormatter={(value) => [
                                config.formatValue(value),
                                config.tooltipTitle
                            ]}
                        />
                    </SparklineWrapper>
                </Tooltip>
            </ContentWrapper>
        </MetricCard>
    );
};